import { Box, Code, Flex, Heading } from '@radix-ui/themes';
import { NotifyHelper } from 'classes/helpers/notify.helper';
import { CommonCallout } from 'components/common/callouts';
import { SuperAdminIcon } from 'components/common/custom-icon/shorthands';
import { CommonDialog } from 'components/common/dialogs';
import { ErrorBoundary } from 'components/common/error-boundary';
import { CommonFormGrid } from 'components/common/form/grid';
import { CommonSearchInput } from 'components/common/form/search';
import { CommonTextInput } from 'components/common/form/text';
import { CommonTextareaInput } from 'components/common/form/textarea';
import { useMachinesPortalStore } from 'components/sections/admin-portal/machines/store/use-machines-portal-store';
import { MachineModelsContext } from 'contexts/admin/machine-models.context';
import { DEFAULT_ACCEPT_BTN } from 'interfaces/i-dialogs';
import { UserRole } from 'lib_ts/enums/auth.enums';
import { BallType } from 'lib_ts/enums/machine.enums';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { IMachineModel } from 'lib_ts/interfaces/modelling/i-machine-model';
import { first } from 'lodash';
import { useContext, useMemo, useState } from 'react';
import { useAuthStore } from 'stores/use-auth-store';

const COMPONENT_NAME = 'CopyModelDialog';

interface IProps {
  model: IMachineModel;
  onClose: () => void;
}

export const CopyModelDialog = ({ model, onClose }: IProps) => {
  const current = useAuthStore((s) => s.current);
  const userRoleFeatureFlags = useAuthStore((s) => s.userRoleFeatureFlags);
  const { loading, copyModel } = useContext(MachineModelsContext);

  const machines = useMachinesPortalStore((s) => s.machines);

  const [newModel, setNewModel] = useState({
    ...model,
    _original_id: model._id,
    name: `${model.name} (Copy)`,
    notes: [model.notes, `Copy of "${model.name}"`]
      .filter((t) => !!t)
      .join('\n'),
    _id: undefined,
  });

  const machineOptions = useMemo(
    () =>
      machines.map((m) => ({
        label: m.machineID,
        value: m.machineID,
      })),
    [machines]
  );

  return (
    <ErrorBoundary identifier={COMPONENT_NAME}>
      <CommonDialog
        identifier={COMPONENT_NAME}
        width={RADIX.DIALOG.WIDTH.MD}
        title="Copy Machine Model"
        buttons={[
          {
            ...DEFAULT_ACCEPT_BTN,
            loading: loading,
            onClick: async () => {
              if (!newModel.name) {
                NotifyHelper.warning({
                  message_md: 'Model name is required.',
                });
                return;
              }

              const success = await copyModel(newModel);

              if (success) {
                onClose();
              }
            },
          },
        ]}
        onClose={onClose}
        content={
          <Flex direction="column" gap={RADIX.FLEX.GAP.MD}>
            <CommonCallout text="Use the following form to copy a machine model (e.g. to be used on a different machine)." />

            <CommonFormGrid columns={1}>
              <CommonSearchInput
                id="copy-model-machineID"
                label="common.machine"
                name="machineID"
                values={newModel.machineID ? [newModel.machineID] : []}
                options={machineOptions}
                onChange={(v) => {
                  const nextMachineID = first(v);

                  setNewModel((prev) => ({
                    ...prev,
                    machineID: nextMachineID ?? '',
                    name: nextMachineID
                      ? prev.name.replace(
                          model.machineID ?? 'N/A',
                          nextMachineID
                        )
                      : prev.name,
                  }));
                }}
                disabled={loading}
                optional={current.role === UserRole.admin}
              />
              <CommonSearchInput
                id="copy-model-ball-type"
                label="common.ball-type"
                name="ball_type"
                values={newModel.ball_type ? [newModel.ball_type] : []}
                options={Object.values(BallType).map((o) => ({
                  label: o,
                  value: o,
                }))}
                onChange={(v) => {
                  const nextBallType = first(v);

                  setNewModel((prev) => ({
                    ...prev,
                    ball_type: nextBallType as BallType,
                    name: nextBallType
                      ? prev.name.replace(prev.ball_type ?? 'N/A', nextBallType)
                      : prev.name,
                  }));
                }}
                disabled={loading}
                optional={current.role === UserRole.admin}
              />
              <CommonTextInput
                id="copy-model-model"
                label="Model Name"
                labelIcon={<SuperAdminIcon />}
                placeholder="Type a unique value"
                value={newModel.name}
                disabled={!userRoleFeatureFlags.adminModels.update || loading}
                onChange={(v) =>
                  setNewModel((prev) => ({ ...prev, name: v ?? '' }))
                }
                hint_md="Changing the machine will reset the model name."
              />
              <CommonTextareaInput
                id="copy-model-notes"
                label="Notes"
                rows={5}
                value={newModel.notes}
                onChange={(value) =>
                  setNewModel((prev) => ({ ...prev, notes: value ?? '' }))
                }
                disabled={loading}
              />

              <Box>
                <Heading size={RADIX.HEADING.SIZE.SM}>Type:</Heading>
                <Code>{model.type}</Code>
              </Box>

              <Box>
                <Heading size={RADIX.HEADING.SIZE.SM}>S3 Path:</Heading>
                <Code>{model.path}</Code>
              </Box>
            </CommonFormGrid>
          </Flex>
        }
      />
    </ErrorBoundary>
  );
};
